import React, { useEffect, useMemo, useRef, useState } from 'react';
import './FormattedText.css';

/**
 * Renderiza texto "enriquecido" de forma SEGURA (sin dangerouslySetInnerHTML,
 * sin parsers de Markdown/HTML de terceros): todo se construye como nodos de
 * React, así que cualquier `<`, `>` o etiqueta que alguien haya escrito en la
 * descripción se muestra tal cual, nunca se interpreta como HTML.
 *
 * Soporta lo mínimo que se usa en las encuestas/notificaciones actuales:
 *   - saltos de línea sueltos (\n)
 *   - párrafos separados por una línea en blanco (\n\n)
 *   - negrita simple *entre asteriscos*
 *   - viñetas ya escritas por el fallero (•, - o *) al inicio de línea:
 *     se conservan tal cual, solo se les da un pequeño sangrado visual
 *   - emojis: es texto plano, se muestran sin tratamiento especial
 *
 * Compatible con encuestas antiguas: si el texto no tiene ningún \n ni *, se
 * muestra exactamente igual que antes (un único párrafo).
 */

const BOLD_SPLIT_RE = /(\*[^*\n]+\*)/g;
const BULLET_RE = /^\s*[•*-]\s+/;

let keySeed = 0;
const nextKey = (prefix) => `${prefix}-${keySeed++}`;

// --- Funciones puras (exportadas para poder testearlas sin renderizar) ----
// El proyecto no tiene @testing-library/react instalado y esta tarea no debe
// añadir dependencias nuevas, así que la lógica de parseo se separa del JSX
// para poder probarla directamente con jest (igual que ya hace
// EncuestasComponent.test.js con canUseSystemBrowserPlugin).

/** Detecta si una línea empieza por una viñeta ya escrita (•, - o *). */
export const isBulletLine = (line) => BULLET_RE.test(line);

/** Divide un texto plano en párrafos (separados por línea(s) en blanco). */
export const splitIntoParagraphs = (text) => {
  const safeText = String(text ?? '').replace(/\r\n/g, '\n').trim();
  if (!safeText) return [];
  return safeText.split(/\n{2,}/).filter((paragraph) => paragraph.trim() !== '');
};

/**
 * Divide una línea en segmentos { bold, text }, respetando *negrita simple*.
 * No interpreta ningún otro HTML/Markdown: todo lo que no sea *negrita* se
 * devuelve como texto plano tal cual, para renderizarlo como nodo de texto
 * de React (nunca como HTML).
 */
export const parseBoldSegments = (line) => {
  const parts = String(line ?? '').split(BOLD_SPLIT_RE).filter((part) => part !== '');
  if (parts.length === 0) return [];

  return parts.map((part) => {
    const boldMatch = /^\*([^*\n]+)\*$/.exec(part);
    return boldMatch ? { bold: true, text: boldMatch[1] } : { bold: false, text: part };
  });
};
// ---------------------------------------------------------------------------

function renderLineSegments(line, lineKeyPrefix) {
  const segments = parseBoldSegments(line);

  return segments.map((segment) => (
    segment.bold
      ? <strong key={nextKey(lineKeyPrefix)}>{segment.text}</strong>
      : <React.Fragment key={nextKey(lineKeyPrefix)}>{segment.text}</React.Fragment>
  ));
}

function renderParagraph(paragraph, paragraphKey) {
  const lines = paragraph.split('\n');

  return (
    <p className="formatted-text-paragraph" key={paragraphKey}>
      {lines.map((line, lineIndex) => {
        const isBullet = isBulletLine(line);
        return (
          <React.Fragment key={`${paragraphKey}-l${lineIndex}`}>
            {lineIndex > 0 && <br />}
            <span className={isBullet ? 'formatted-text-bullet-line' : undefined}>
              {renderLineSegments(line, `${paragraphKey}-l${lineIndex}`)}
            </span>
          </React.Fragment>
        );
      })}
    </p>
  );
}

/**
 * @param {string} text - descripción tal cual viene guardada (texto plano).
 * @param {string} [className] - clases extra para el contenedor.
 * @param {boolean} [collapsible] - si es true, muestra un resumen recortado
 *   con botón "Ver información completa" / "Ver menos".
 * @param {number} [collapsedHeight] - alto máximo (px) cuando está colapsado.
 */
export const FormattedText = ({
  text,
  className = '',
  collapsible = false,
  collapsedHeight = 170,
}) => {
  const [expanded, setExpanded] = useState(!collapsible);
  const rootRef = useRef(null);

  const paragraphs = useMemo(() => splitIntoParagraphs(text), [text]);

  useEffect(() => {
    if (!collapsible || !rootRef.current) return undefined;

    const panel = rootRef.current.closest('.encuesta-detalle, .poll-detail');
    if (!panel || !panel.style.height || panel.style.height === 'auto') return undefined;

    let animationFrame = window.requestAnimationFrame(() => {
      panel.style.height = `${panel.scrollHeight}px`;
    });

    const setAutoHeight = (event) => {
      if (event.propertyName !== 'height') return;
      panel.style.height = 'auto';
      panel.removeEventListener('transitionend', setAutoHeight);
    };

    panel.addEventListener('transitionend', setAutoHeight);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      panel.removeEventListener('transitionend', setAutoHeight);
    };
  }, [collapsible, expanded, paragraphs.length]);

  if (!paragraphs.length) return null;

  const isCollapsed = collapsible && !expanded;

  return (
    <div ref={rootRef} className={`formatted-text ${className}`.trim()}>
      <div
        className={`formatted-text-body${isCollapsed ? ' is-collapsed' : ''}`}
        style={isCollapsed ? { maxHeight: collapsedHeight } : undefined}
      >
        {paragraphs.map((paragraph, index) => renderParagraph(paragraph, `p${index}`))}
      </div>

      {collapsible && (
        <button
          type="button"
          className="formatted-text-toggle"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
        >
          {expanded ? 'Ver menos' : 'Ver información completa'}
        </button>
      )}
    </div>
  );
};

export default FormattedText;
